<div class="view-chargeback-dialog flex max-h-[inherit] flex-col h-full" role="dialog"
  aria-labelledby="chargeback-details-title">
  <!-- Header -->
  <header class="shrink-0 border-b border-[#ECECEC] pb-4">
    <div class="mb-3 flex justify-between items-center">
      <h2 id="chargeback-details-title" class="text-xl font-semibold tracking-tight text-[#17191C]">
        Charge Back Details
      </h2>
      <button type="button" mat-dialog-close class="!rounded-full" aria-label="Close chargeback details">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true">
          <circle opacity="0.05" cx="20" cy="20" r="20" fill="#E71D36" />
          <path opacity="0.8"
            d="M20.9161 19.9999L24.8099 23.8939C25.0634 24.1472 25.0634 24.5567 24.8099 24.81C24.5567 25.0633 24.1471 25.0633 23.8939 24.81L19.9999 20.916L16.1061 24.81C15.8527 25.0633 15.4433 25.0633 15.1901 24.81C14.9366 24.5567 14.9366 24.1472 15.1901 23.8939L19.0839 19.9999L15.1901 16.1059C14.9366 15.8526 14.9366 15.4431 15.1901 15.1898C15.3163 15.0635 15.4822 15 15.6481 15C15.8139 15 15.9798 15.0635 16.1061 15.1898L19.9999 19.0838L23.8939 15.1898C24.0202 15.0635 24.1861 15 24.3519 15C24.5178 15 24.6836 15.0635 24.8099 15.1898C25.0634 15.4431 25.0634 15.8526 24.8099 16.1059L20.9161 19.9999Z"
            fill="#E71D36" />
        </svg>
      </button>
    </div>

    <div class="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
      <span class="text-base font-semibold text-[#17191C]">{{ headerName }}</span>
      @if (parsedStatusLabel) {
      <span [class]="statusChipClass">{{ parsedStatusLabel }}</span>
      } @else {
      @for (statusBadge of statusBadges; track statusBadge) {
      <span class="inline-flex w-fit rounded-full bg-[#E8F1FF] px-3 py-1 text-xs font-medium text-[#2563EB]">
        {{ statusBadge }}
      </span>
      }
      }
    </div>
  </header>

  <div class="min-h-0 flex-1 overflow-y-auto py-5 pr-0.5">
    @if (stateBanner) {
    <div class="vb-state-banner" [attr.data-tone]="stateBanner.tone" role="status">
      <p class="vb-state-banner__text">{{ stateBanner.message }}</p>
    </div>
    }

    <section class="mb-8" aria-label="Case details">
      <div class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">Case Ref</span>
          <span class="text-sm font-medium text-[#17191C] break-all">{{ caseRef }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">{{ 'Action Deadline' }}</span>
          <span [class]="daysRowValueClass">{{ actionDeadLine | date : 'medium' }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">Date</span>
          <span class="text-sm font-medium text-[#17191C]">{{ formattedDate }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">Transaction ID</span>
          <span class="flex flex-wrap items-center gap-2 text-sm font-medium text-[#17191C]">
            <span class="break-all">{{ transactionId }}</span>
            @if (transactionId !== '—') {
            <button type="button" class="shrink-0 text-primary hover:opacity-80"
              [attr.aria-label]="'Copy transaction ID ' + transactionId" (click)="copyTransactionId()">
              <lib-copy-icon></lib-copy-icon>
            </button>
            }
          </span>
        </div>
        @if (merchantName) {
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">Merchant Name</span>
          <span class="text-sm font-medium text-[#17191C]">{{ merchantName }}</span>
        </div>
        }
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">Customer Name</span>
          <span class="text-sm font-medium text-[#17191C]">{{ customerName }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">Amount</span>
          <span class="text-sm font-medium text-[#17191C] tabular-nums">{{ amountDisplay }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-gray-500">Resolved Provider</span>
          <span class="text-sm font-medium text-[#17191C]">{{ resolvedProvider }}</span>
        </div>
      </div>

      <div class="mt-5">
        <span class="text-xs text-gray-500">Reason</span>
        <div class="mt-2 rounded-xl bg-[#F4F5F7] px-4 py-3 text-sm leading-relaxed text-[#434C53]">
          @if (reasonTextCore) {
          {{ reasonTextCore }}
          } @else {
          <span class="text-gray-400">No reason provided.</span>
          }
        </div>
      </div>
    </section>

    <section aria-label="Evidence repository">
      <h3 class="mb-4 text-base font-semibold text-[#17191C]">
        Evidence Repository
      </h3>

      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div class="flex flex-col gap-4">
          <h4 class="text-sm font-semibold text-[#17191C]">
            Merchant Response
          </h4>
          @if (merchantResponseText) {
          <div class="rounded-xl bg-[#F4F5F7] px-4 py-3 text-sm leading-relaxed text-[#434C53]">
            {{ merchantResponseText }}
          </div>
          }

          @if (merchantEvidenceItems.length === 0) {
          <p class="text-sm text-gray-400">No merchant files attached.</p>
          } @else {
          @for (file of merchantEvidenceItems; track $index) {
          <div class="flex items-center gap-3 rounded-xl border border-[#E6EAEE] bg-white px-3 py-3">
            @if (file.kind === 'image') {
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600"
              aria-hidden="true">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3A1.5 1.5 0 0 0 1.5 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
              </svg>
            </span>
            } @else {
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600"
              aria-hidden="true">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            </span>
            }
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-medium text-[#17191C]">
                {{ file.title }}
              </p>
              @if (file.subtitle) {
              <p class="truncate text-xs text-gray-500">{{ file.subtitle }}</p>
              }
            </div>
            <button type="button"
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-primary"
              [attr.aria-label]="
                    file.action === 'view' ? 'View ' + file.title : 'Download ' + file.title
                  " (click)="openLink(file.url)">
              @if (file.action === 'view') {
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"
                aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
              } @else {
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"
                aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              }
            </button>
          </div>
          }
          }
        </div>

        <div class="flex flex-col gap-4">
          <h4 class="text-sm font-semibold text-[#17191C]">
            Customer Description
          </h4>
          @if (customerDescriptionText) {
          <div class="rounded-xl bg-[#F4F5F7] px-4 py-3 text-sm leading-relaxed text-[#434C53]">
            {{ customerDescriptionText }}
          </div>
          }
          <h4 class="text-sm font-semibold text-[#17191C]">Customer Evidence</h4>
          @if (customerEvidenceItems.length === 0) {
          <p class="text-sm text-gray-400">No customer files attached.</p>
          } @else {
          @for (file of customerEvidenceItems; track $index) {
          <div class="flex items-center gap-3 rounded-xl border border-[#E6EAEE] bg-white px-3 py-3">
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600"
              aria-hidden="true">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            </span>
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-medium text-[#17191C]">
                {{ file.title }}
              </p>
              @if (file.subtitle) {
              <p class="truncate text-xs text-gray-500">{{ file.subtitle }}</p>
              }
            </div>
            <button type="button"
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-primary"
              [attr.aria-label]="'Download ' + file.title" (click)="openLink(file.url)">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"
                aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
            </button>
          </div>
          }
          }
        </div>
      </div>
    </section>
  </div>

  @if (showFooterActions) {
  <footer class="shrink-0 border-t border-[#ECECEC] pt-4 mt-auto">
    <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
      <app-button type="button" [inputClass]="
          '!mt-0 !border !border-[#E71D36] !bg-white !text-[#E71D36] hover:!bg-red-50 sm:!w-auto'
        " (buttonClick)="onDecline()">
        {{ secondaryLabel }}
      </app-button>
      <app-button type="button" [inputClass]="'!mt-0 sm:!w-auto'" (buttonClick)="onAccept()">
        {{ primaryLabel }}
      </app-button>
    </div>
  </footer>
  }

  @if (showOfficerFooterActions) {
  <footer class="shrink-0 border-t border-[#ECECEC] pt-4 mt-auto">
    <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
      <app-button type="button" [inputClass]="
          '!mt-0 !border !border-[#E71D36] !bg-white !text-[#E71D36] hover:!bg-red-50 sm:!w-auto'
        " (buttonClick)="onOfficerDecline()">
        {{ officerSecondaryLabel }}
      </app-button>
      <app-button type="button" [inputClass]="'!mt-0 sm:!w-auto'" (buttonClick)="onOfficerApprove()">
        {{ officerPrimaryLabel }}
      </app-button>
    </div>
  </footer>
  }
</div>